// M06 /diagnosticos/:id/relatorios: real PDF/CSV generation from the server, with the completion summary.
import { useState } from "react";
import { FileDown, FileSpreadsheet, FileText } from "lucide-react";
import { Button, Panel, Pill } from "../../components/ui.tsx";
import { useToast } from "../../components/toast.tsx";
import { download } from "../../lib/api.ts";
import { mBase, type AssessmentDetail, type MaturityInstitution } from "../../lib/maturity.ts";
import { AssessmentShell } from "./AssessmentShell.tsx";
import { WithInstitution } from "./MaturityLayout.tsx";
import { formatInstantBR } from "../../../shared/dates.ts";

export function MaturityReportsPage({ id }: { id: string }) {
  return (
    <WithInstitution>
      {(inst) => <AssessmentShell institution={inst} id={id} tab="relatorios" title="Relatórios">{(a) => <ReportsBody institution={inst} assessment={a} />}</AssessmentShell>}
    </WithInstitution>
  );
}

const state = (s: AssessmentDetail["scenarios"]["diagnosis"], none: string) =>
  !s ? none : s.state === "finalized" ? `encerrado em ${formatInstantBR(s.finalized_at)}` : `rascunho · ${s.answered} de ${s.total} quesitos respondidos`;

function ReportsBody({ institution, assessment: a }: { institution: MaturityInstitution; assessment: AssessmentDetail }) {
  const toast = useToast();
  const [comments, setComments] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  async function run(file: string) {
    setBusy(file);
    try {
      await download(`${mBase(institution.id)}/assessments/${a.id}/exports/${file}`, { include_comments: comments });
    } catch (e) {
      toast("danger", e instanceof Error ? e.message : "Não foi possível gerar o arquivo.");
    } finally {
      setBusy(null);
    }
  }
  const cards = [
    { file: "diagnosis.pdf", Icon: FileText, title: "Relatório de diagnóstico (PDF)", text: "Nota geral, gauges por subdimensão, tabela numérica e respostas por quesito." },
    { file: "comparison.pdf", Icon: FileDown, title: "Relatório comparativo (PDF)", text: "Diagnóstico e prognóstico lado a lado, com dois ponteiros e a diferença em pontos percentuais." },
    { file: "responses.csv", Icon: FileSpreadsheet, title: "Respostas (CSV)", text: "Uma linha por quesito, com os dois cenários lado a lado, para planilhas." },
  ];
  return (
    <div>
      <Panel className="mb-5">
        <h2 className="font-bold">Situação dos cenários</h2>
        <ul className="mt-2 space-y-1 text-sm">
          <li>Diagnóstico: {state(a.scenarios.diagnosis, "—")}</li>
          <li>Prognóstico: {state(a.scenarios.prognosis, "sem prognóstico registrado")}</li>
        </ul>
        {(a.scenarios.diagnosis?.state === "draft" || a.scenarios.prognosis?.state === "draft") && (
          <p className="mt-2 text-sm text-muted"><Pill tone="gold">Rascunho</Pill> Cenários em rascunho saem marcados como RASCUNHO, com os valores pendentes indicados.</p>
        )}
        <label className="mt-4 flex min-h-10 items-center gap-2 text-sm">
          <input type="checkbox" className="size-4 accent-primary" checked={comments} onChange={(e) => setComments(e.target.checked)} />
          Incluir unidade avaliadora e comentários
        </label>
      </Panel>
      <ul className="grid gap-4 md:grid-cols-3">
        {cards.map((c) => (
          <li key={c.file}>
            <Panel as="article" className="flex h-full flex-col">
              <c.Icon className="size-5 text-primary" aria-hidden />
              <h3 className="mt-2 font-semibold">{c.title}</h3>
              <p className="mt-1 text-sm text-muted">{c.text}</p>
              <div className="mt-auto pt-4"><Button loading={busy === c.file} disabled={busy !== null} onClick={() => void run(c.file)}>Baixar</Button></div>
            </Panel>
          </li>
        ))}
      </ul>
    </div>
  );
}
